import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { type IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faAngleDown, faAngleUp } from '@fortawesome/pro-regular-svg-icons';
import { NavigationService } from '@reconcept-platform/shared/navigation/nav-content';
import { RcpPopoverDirective } from '@reconcept-platform/shared/ui/standalone/popover';
import { NavListContainerComponent } from '../nav-list-container/nav-list-container.component';

@Component({
  selector: 'rcp-nav-list-group',
  template: `
    @if (label() || expandable()) {
      <div class="list-group">
        <div
          class="list-group__header"
          [class.expandable]="expandable()"
          [class.clickable]="expandable()"
          (click)="expand()"
          [rcpPopover]="label()"
          rcpPopoverPlacement="right"
          [rcpPopoverIsEnabled]="collapsed()"
        >
          @if (!collapsed()) {
            <!-- note: spaces before and after the text  in this element will be shown 
                if max-2-lines is active, because of the special box rendering -->
            <span class="list-group__header__label text max-2-lines">{{ label() }}</span>
          }

          @if (expandable()) {
            <fa-icon [icon]="expanded() ? faAngleUp : faAngleDown" />
          }
        </div>
      </div>
    }
    <div [class.expanded]="expanded() || !expandable()" class="list-group__items">
      <ng-content />
    </div>
  `,
  styleUrl: './nav-list-group.component.scss',
  imports: [FaIconComponent, RcpPopoverDirective],
  host: {
    '[class.collapsed]': 'collapsed()',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class NavListGroupComponent {
  private navigation = inject(NavigationService);

  private navListContainer = inject(NavListContainerComponent, { host: true });

  label = input<string>();
  expandable = input<boolean>(false);
  startExpanded = input<boolean>(true);

  expanded = linkedSignal(() => this.startExpanded() || !this.expandable());

  collapsed = computed(() => (this.collapsable() ? this.navigation.sidebarIsCollapsed() : false));
  collapsable = computed<boolean>(() => this.navListContainer.collapsable());

  // @ts-expect-error TS2322 type not assignable
  icon = computed<IconDefinition>(() => (this.collapsed() && this.collapseIcon() ? this.collapseIcon() : undefined));
  // @ts-expect-error TS2345 argument not assignable
  collapseIcon = input<IconDefinition>(undefined);

  readonly expandedChange = output<boolean>();

  constructor() {
    effect(() => {
      if (this.expandable()) {
        this.expandedChange.emit(this.expanded());
      }
    });
  }

  expand() {
    if (this.expandable()) {
      this.expanded.update((expanded) => !expanded);
    }
  }

  protected readonly faAngleUp = faAngleUp;
  protected readonly faAngleDown = faAngleDown;
}
